import { useState, useCallback } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  GET_MY_STORE, UPDATE_MY_STORE_PREFIX, CHECK_STORE_PREFIX,
  GET_MY_STORE_API_KEY, GENERATE_MY_STORE_API_KEY, REVOKE_MY_STORE_API_KEY,
  GET_MODEL_SETTING, ADD_MODEL, UPDATE_MODEL, DELETE_MODEL,
  GET_POSE_LISTS, DELETE_POSE_LIST,
  GET_MASTER_POSES_FOR_CLIENT, SAVE_POSE_FROM_MASTER,
  GET_MODEL_EXPRESSION_LISTS, DELETE_MODEL_EXPRESSION_LIST,
  GET_MASTER_MODEL_EXPRESSIONS_FOR_CLIENT, SAVE_MODEL_EXPRESSION_FROM_MASTER,
  GET_BACKGROUNDS, ADD_BACKGROUND, DELETE_BACKGROUND,
  GET_LIGHTINGS, ADD_LIGHTING, DELETE_LIGHTING,
  GET_DIMENSIONS, CREATE_DIMENSION, SET_ACTIVE_DIMENSION, DELETE_DIMENSION,
  GET_LOGO_SETTING, ADD_LOGO, DELETE_LOGO,
  GET_POSITIONS, ADD_POSITION, DELETE_POSITION,
  GET_TEXT_SETTING, UPDATE_TEXT_SETTING,
  GET_ATTRIBUTES, GET_PREFERENCE_VARIANT, SAVE_PREFERENCE_VARIANT,
  GET_GROUP_PREFERENCE_VARIANT, SAVE_GROUP_PREFERENCE_VARIANT,
  GET_PRODUCT_CATEGORIES, DELETE_PRODUCT_CATEGORY,
  GET_MASTER_PRODUCT_CATEGORIES_FOR_CLIENT, SAVE_PRODUCT_CATEGORY_FROM_MASTER,
  GET_DESCRIPTION_TEMPLATES, GET_MASTER_DESCRIPTION_TEMPLATES_FOR_CLIENT,
  SAVE_DESCRIPTION_TEMPLATE_FROM_MASTER, DELETE_DESCRIPTION_TEMPLATE, UPDATE_DESCRIPTION_TEMPLATE,
} from '../graphql/queries';
import { uploadImage } from '../services/uploadService';

// ── Helpers ────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  if (!msg) return null;
  return (
    <div className={`toast toast--${type}`} onClick={onClose}>
      {msg}
    </div>
  );
}

function useToast() {
  const [toast, setToast] = useState(null);
  const show = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };
  return [toast, show];
}

// ── AddDrawer ──────────────────────────────────────────────────
// Slide-in panel used by Poses, Model Expression and Product Category tabs
// to present master-library items for selection. Kept always-mounted and
// animated via transform so the slide transition plays both in and out.
function AddDrawer({
  open,
  onClose,
  title,
  subtitle,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  children,
}) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
          opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 220ms ease-out', zIndex: 999,
        }}
      />
      <aside
        style={{
          position: 'fixed', top: 0, right: 0, height: '100%', width: '420px',
          maxWidth: '100vw', background: '#fff',
          boxShadow: '-8px 0 28px rgba(15, 23, 42, 0.12)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 260ms cubic-bezier(0.32, 0.72, 0, 1)',
          zIndex: 1000, display: 'flex', flexDirection: 'column',
        }}
      >
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0',
        }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>{title}</h3>
            {subtitle && (
              <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: '#64748b' }}>{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: '#64748b', display: 'flex', alignItems: 'center',
            }}
          >
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 22, height: 22 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        {onSearchChange && (
          <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ position: 'relative' }}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 103.5 10.5a7.5 7.5 0 0013.15 6.15z" />
              </svg>
              <input
                className="form-input"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                style={{ paddingLeft: 34, width: '100%' }}
              />
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
          {children}
        </div>
      </aside>
    </>
  );
}

function EmptyDrawerMessage({ children }) {
  return (
    <div style={{
      border: '1px dashed #e2e8f0', borderRadius: '0.5rem', padding: '1.25rem',
      textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem',
    }}>
      {children}
    </div>
  );
}

const TABS = [
  { key: 'store', label: 'Store' },
  { key: 'model', label: 'Model' },
  { key: 'poses', label: 'Poses' },
  { key: 'modelExpression', label: 'Model Expression' },
  { key: 'background', label: 'Background' },
  { key: 'lighting', label: 'Lighting' },
  { key: 'position', label: 'Position' },
  { key: 'dimensions', label: 'Dimensions' },
  { key: 'logo', label: 'Logo' },
  { key: 'text', label: 'Text' },
  { key: 'preference', label: 'Preference' },
  { key: 'groupPreference', label: 'Grouping Preference' },
  { key: 'productCategory', label: 'Product Category' },
  { key: 'descriptionTemplate', label: 'Description Template' },
];

// ── Store Tab ──────────────────────────────────────────────────
// Masks every char of a long secret except the last 4. The fresh value
// (right after generation) bypasses this so the admin can copy it.
function maskApiKey(key) {
  if (!key) return '';
  if (key.length <= 8) return '••••••••';
  return `${key.slice(0, 4)}${'•'.repeat(Math.max(8, key.length - 8))}${key.slice(-4)}`;
}

// ── External API Key panel ─────────────────────────────────────
// Lives in the Store tab. Lets the admin generate / copy / revoke a
// per-store secret used by external systems on the REST endpoints.
function ApiKeySection({ showToast }) {
  const { data, loading, refetch } = useQuery(GET_MY_STORE_API_KEY, { fetchPolicy: 'network-only' });
  const [generateKey, { loading: generating }] = useMutation(GENERATE_MY_STORE_API_KEY);
  const [revokeKey, { loading: revoking }] = useMutation(REVOKE_MY_STORE_API_KEY);

  // Stored separately so a freshly generated key can stay visible (unmasked)
  // until the page is reloaded. The server returns the same key on subsequent
  // GET_MY_STORE_API_KEY calls, but we mask it then for safety.
  const [revealed, setRevealed] = useState(null);

  const storedKey = data?.getMyStoreApiKey?.apiKey || null;
  const generatedAt = data?.getMyStoreApiKey?.apiKeyGeneratedAt;
  const display = revealed ?? (storedKey ? maskApiKey(storedKey) : null);

  const handleGenerate = async () => {
    const replacing = !!storedKey;
    if (replacing) {
      const ok = window.confirm(
        'Generating a new key will revoke the existing one. Any external system using the old key will stop working until you update it. Continue?'
      );
      if (!ok) return;
    }
    const { data: res } = await generateKey();
    const payload = res?.generateMyStoreApiKey;
    if (payload?.error) {
      showToast(payload.message || 'Failed to generate key', 'error');
      return;
    }
    setRevealed(payload.apiKey);
    showToast(replacing ? 'API key rotated' : 'API key generated');
    refetch();
  };

  const handleRevoke = async () => {
    const ok = window.confirm(
      'Revoke the current API key? External systems using it will stop working immediately.'
    );
    if (!ok) return;
    const { data: res } = await revokeKey();
    const payload = res?.revokeMyStoreApiKey;
    if (payload?.error) {
      showToast(payload.message || 'Failed to revoke', 'error');
      return;
    }
    setRevealed(null);
    showToast('API key revoked');
    refetch();
  };

  const handleCopy = async () => {
    const toCopy = revealed || storedKey;
    if (!toCopy) return;
    try {
      await navigator.clipboard.writeText(toCopy);
      showToast('Copied to clipboard');
    } catch {
      showToast('Copy failed — select and copy manually', 'error');
    }
  };

  return (
    <div style={{
      marginBottom: '1.5rem',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
      border: '1px solid #e2e8f0',
    }}>
      <div style={{ marginBottom: '0.5rem' }}>
        <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
          External API Key
        </h3>
        <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '0.2rem 0 0' }}>
          Use this key in the <code style={{ background: '#e2e8f0', padding: '1px 4px', borderRadius: 3 }}>x-api-key</code> header to call REST endpoints like <code style={{ background: '#e2e8f0', padding: '1px 4px', borderRadius: 3 }}>GET /api/v1/staff</code>.
        </p>
      </div>

      {loading ? (
        <div style={{ fontSize: '0.85rem', color: '#94a3b8', padding: '0.5rem 0' }}>Loading…</div>
      ) : !storedKey && !revealed ? (
        <div style={{ marginTop: '0.75rem' }}>
          <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '0 0 0.5rem 0' }}>
            No API key generated yet.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Generating…' : 'Generate API Key'}
          </button>
        </div>
      ) : (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.55rem 0.75rem',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '0.5rem',
            fontFamily: 'monospace',
            fontSize: '0.82rem',
            color: '#1e293b',
            wordBreak: 'break-all',
          }}>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</span>
            <button
              type="button"
              onClick={handleCopy}
              title="Copy"
              style={{
                background: 'transparent',
                border: '1px solid #cbd5e1',
                borderRadius: '0.35rem',
                padding: '0.25rem 0.55rem',
                cursor: 'pointer',
                fontSize: '0.72rem',
                color: '#475569',
              }}
            >Copy</button>
          </div>

          {revealed && (
            <p style={{ fontSize: '0.72rem', color: '#10b981', marginTop: '0.4rem' }}>
              ✓ This is the only time the full key is shown — copy it now and store it somewhere safe.
            </p>
          )}

          {generatedAt && (
            <p style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: revealed ? '0.2rem' : '0.4rem' }}>
              Generated {new Date(generatedAt).toLocaleString()}
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? 'Generating…' : 'Regenerate'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleRevoke}
              disabled={revoking}
              style={{ color: '#dc2626', borderColor: '#fecaca' }}
            >
              {revoking ? 'Revoking…' : 'Revoke'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StoreTab() {
  const [toast, showToast] = useToast();
  const [prefixInput, setPrefixInput] = useState('');
  const [prefixStatus, setPrefixStatus] = useState(null); // null | 'available' | 'taken' | 'invalid'
  const [saving, setSaving] = useState(false);

  const { data: storeData, loading: storeLoading, refetch: refetchStore } = useQuery(GET_MY_STORE, {
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      if (d?.getMyStore?.prefix) setPrefixInput(d.getMyStore.prefix);
    },
  });
  const [checkPrefix] = useLazyQuery(CHECK_STORE_PREFIX, { fetchPolicy: 'network-only' });
  const [updatePrefix] = useMutation(UPDATE_MY_STORE_PREFIX);

  const store = storeData?.getMyStore;
  const currentPrefix = store?.prefix || '';

  const handlePrefixChange = useCallback(async (value) => {
    const v = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setPrefixInput(v);
    setPrefixStatus(null);
    if (v.length < 2) return;
    if (v === currentPrefix) { setPrefixStatus('available'); return; }
    const { data } = await checkPrefix({ variables: { prefix: v } });
    if (data?.checkStorePrefix?.available) {
      setPrefixStatus('available');
    } else {
      setPrefixStatus('taken');
    }
  }, [checkPrefix, currentPrefix]);

  const handleSavePrefix = async () => {
    const v = prefixInput.trim().toUpperCase();
    if (!v || v.length < 2 || v.length > 8) {
      showToast('Prefix must be 2–8 uppercase letters or digits', 'error');
      return;
    }
    if (prefixStatus === 'taken') {
      showToast('This prefix is already taken', 'error');
      return;
    }
    setSaving(true);
    const { data } = await updatePrefix({ variables: { prefix: v } });
    setSaving(false);
    if (data?.updateMyStorePrefix?.error) {
      showToast(data.updateMyStorePrefix.message, 'error');
    } else {
      showToast('Prefix updated successfully');
      refetchStore();
    }
  };

  if (storeLoading) return <div style={{ padding: '2rem', color: '#94a3b8' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: '480px' }}>
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => {}} />

      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.25rem' }}>Store Info</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
          {[
            { label: 'Store Name', value: store?.storeName },
            { label: 'Store ID', value: store?.storeId, mono: true },
            { label: 'Store Type', value: store?.storeType },
            { label: 'Default Resolution', value: store?.defaultResolution },
          ].map((f) => (
            <div key={f.label} style={{ padding: '0.65rem 0.85rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>{f.label}</div>
              <div style={{ fontSize: '0.88rem', color: '#1e293b', fontFamily: f.mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{f.value || '—'}</div>
            </div>
          ))}
        </div>
      </div>

      <ApiKeySection showToast={showToast} />

      <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Store Prefix</h3>
          <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '0.2rem 0 0' }}>
            Used to auto-generate staff Login IDs (e.g. <code style={{ background: '#e2e8f0', padding: '1px 4px', borderRadius: 3 }}>{prefixInput || 'SHOP'}-001</code>).
            Must be unique across all stores.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <input
              className="form-input"
              style={{ fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em' }}
              value={prefixInput}
              onChange={(e) => handlePrefixChange(e.target.value)}
              placeholder="e.g. SHOP"
              maxLength={8}
            />
            {prefixStatus === 'taken' && (
              <div style={{ fontSize: '0.75rem', color: '#f43f5e', marginTop: '4px' }}>Prefix already in use</div>
            )}
            {prefixStatus === 'available' && prefixInput !== currentPrefix && (
              <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '4px' }}>Prefix available</div>
            )}
          </div>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSavePrefix}
            disabled={saving || prefixStatus === 'taken' || prefixInput === currentPrefix || prefixInput.length < 2}
            style={{ padding: '0 0.9rem', height: '38px' }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const GENDER_OPTIONS = ['male', 'female', 'kid-boy', 'kid-girl', 'unisex', 'plus-size-male', 'plus-size-female'];

// ── Model Tab ──────────────────────────────────────────────────
function ModelTab() {
  const [toast, showToast] = useToast();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', file: null, gender: '' });
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', file: null, gender: '' });
  const [editUploading, setEditUploading] = useState(false);

  const { data, loading, refetch } = useQuery(GET_MODEL_SETTING, { fetchPolicy: 'network-only' });
  const [addModel] = useMutation(ADD_MODEL);
  const [updateModel] = useMutation(UPDATE_MODEL);
  const [deleteModel] = useMutation(DELETE_MODEL);

  const models = data?.getModelSetting?.models || [];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setForm((f) => ({ ...f, file }));
  };

  const handleAdd = async () => {
    if (!form.name.trim() || !form.file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(form.file, 'model');
      const res = await addModel({ variables: { name: form.name.trim(), url, gender: form.gender || null } });
      if (res.data?.addModel?.error) showToast(res.data.addModel.message, 'error');
      else { showToast('Model added'); setForm({ name: '', file: null, gender: '' }); setAdding(false); refetch(); }
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditForm({ name: m.name, file: null, gender: m.gender || '' });
  };

  const handleEdit = async (modelId) => {
    if (!editForm.name.trim()) return;
    setEditUploading(true);
    try {
      const variables = { modelId, name: editForm.name.trim(), gender: editForm.gender || null };
      if (editForm.file) {
        const { url } = await uploadImage(editForm.file, 'model');
        variables.url = url;
      }
      const res = await updateModel({ variables });
      if (res.data?.updateModel?.error) showToast(res.data.updateModel.message, 'error');
      else { showToast('Model updated'); setEditingId(null); setEditForm({ name: '', file: null, gender: '' }); refetch(); }
    } catch (err) {
      showToast(err.message || 'Update failed', 'error');
    } finally {
      setEditUploading(false);
    }
  };

  const handleDelete = async (modelId) => {
    const res = await deleteModel({ variables: { modelId } });
    if (res.data?.deleteModel?.error) showToast(res.data.deleteModel.message, 'error');
    else { showToast('Model deleted'); refetch(); }
  };

  const cancelAdd = () => { setForm({ name: '', file: null, gender: '' }); setAdding(false); };

  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />
      <div className="settings-section-header">
        <h3 className="settings-section-title">Models</h3>
        <button className="btn btn--primary" onClick={() => setAdding(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Model
        </button>
      </div>

      {adding && (
        <div className="bg-add-form">
          <input
            className="form-input"
            placeholder="Model name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <select
            className="form-input"
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
          >
            <option value="">Select gender</option>
            {GENDER_OPTIONS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <div className="bg-upload-area">
            {form.file ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width={20} height={20} style={{ color: 'var(--primary-light)', flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span style={{ fontSize: '0.875rem', color: 'var(--slate-300)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.file.name}</span>
                <button className="btn btn--ghost" style={{ fontSize: '0.75rem', flexShrink: 0 }} onClick={() => setForm({ ...form, file: null })}>Remove</button>
              </div>
            ) : (
              <label className="bg-upload-label">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width={24} height={24}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>Click to upload image (JPEG, PNG, WEBP)</span>
                <input type="file" accept="image/jpeg,image/png,image/jpg,image/webp" onChange={handleFileChange} style={{ display: 'none' }} />
              </label>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn--primary" onClick={handleAdd} disabled={uploading || !form.name.trim() || !form.file}>
              {uploading ? 'Uploading...' : 'Add'}
            </button>
            <button className="btn btn--ghost" onClick={cancelAdd}>Cancel</button>
          </div>
        </div>
      )}

      {models.length === 0 && !adding ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <p className="empty-state-title">No models</p>
          <p className="empty-state-desc">Upload an image to get started</p>
        </div>
      ) : (
        <div className="dim-list">
          {models.map((m) => (
            <div key={m.id} className="dim-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="dim-item-info" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <img
                    src={m.url}
                    alt={m.name}
                    style={{ width: '2.5rem', height: '2.5rem', objectFit: 'cover', borderRadius: '0.375rem', flexShrink: 0, border: '1px solid var(--slate-200)' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span className="dim-item-name">{m.name}</span>
                  {m.gender && <span style={{ fontSize: '0.75rem', color: 'var(--slate-400)', background: 'var(--slate-700)', padding: '2px 6px', borderRadius: '4px' }}>{m.gender}</span>}
                </div>
                <div className="dim-item-actions" style={{ display: 'flex', gap: '4px' }}>
                  <button className="row-btn" onClick={() => startEdit(m)} title="Edit">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button className="row-btn row-btn--delete" onClick={() => handleDelete(m.id)}>
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              {editingId === m.id && (
                <div className="bg-add-form" style={{ marginTop: '0.75rem' }}>
                  <input
                    className="form-input"
                    placeholder="Model name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    autoFocus
                  />
                  <select
                    className="form-input"
                    value={editForm.gender}
                    onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                  >
                    <option value="">Select gender</option>
                    {GENDER_OPTIONS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  <div className="bg-upload-area">
                    {editForm.file ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width={20} height={20} style={{ color: 'var(--primary-light)', flexShrink: 0 }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span style={{ fontSize: '0.875rem', color: 'var(--slate-300)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{editForm.file.name}</span>
                        <button className="btn btn--ghost" style={{ fontSize: '0.75rem', flexShrink: 0 }} onClick={() => setEditForm({ ...editForm, file: null })}>Remove</button>
                      </div>
                    ) : (
                      <label className="bg-upload-label">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width={24} height={24}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span>Click to upload new image (optional)</span>
                        <input type="file" accept="image/jpeg,image/png,image/jpg,image/webp" onChange={(e) => { const f = e.target.files[0]; if (f) setEditForm({ ...editForm, file: f }); }} style={{ display: 'none' }} />
                      </label>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn--primary" onClick={() => handleEdit(m.id)} disabled={editUploading || !editForm.name.trim()}>
                      {editUploading ? 'Saving...' : 'Save'}
                    </button>
                    <button className="btn btn--ghost" onClick={() => { setEditingId(null); setEditForm({ name: '', file: null }); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Poses Tab ──────────────────────────────────────────────────
function PosesTab() {
  const [toast, showToast] = useToast();
  const [showSelector, setShowSelector] = useState(false);
  const [savingMasterId, setSavingMasterId] = useState(null);
  const [search, setSearch] = useState('');

  const { data, loading, refetch } = useQuery(GET_POSE_LISTS, { fetchPolicy: 'network-only' });
  const { data: masterData, loading: masterLoading } = useQuery(GET_MASTER_POSES_FOR_CLIENT, { fetchPolicy: 'network-only' });
  const [deletePoseList] = useMutation(DELETE_POSE_LIST);
  const [savePoseFromMaster] = useMutation(SAVE_POSE_FROM_MASTER);

  const poses = data?.getPoseLists?.poseLists || [];
  const masterPoses = masterData?.getMasterPosesForClient?.masterPoses || [];
  const savedMasterIds = poses.filter((p) => p.masterPoseId).map((p) => p.masterPoseId);
  const unsavedMasterPoses = masterPoses.filter((mp) => !savedMasterIds.includes(mp.id));
  const q = search.trim().toLowerCase();
  const filteredUnsavedMasterPoses = q
    ? unsavedMasterPoses.filter((mp) => (mp.name || '').toLowerCase().includes(q))
    : unsavedMasterPoses;

  const handleDelete = async (id) => {
    const res = await deletePoseList({ variables: { id } });
    if (res.data?.deletePoseList?.error) showToast(res.data.deletePoseList.message, 'error');
    else { showToast('Removed'); refetch(); }
  };

  const handleSaveFromMaster = async (mp) => {
    setSavingMasterId(mp.id);
    try {
      const input = {
        name: mp.name,
        imageUrl: mp.imageUrl || '',
        framing: mp.framing || '',
        angle: mp.angle || '',
        poseDetail: mp.poseDetail || '',
      };
      const res = await savePoseFromMaster({ variables: { masterPoseId: mp.id, input } });
      if (res.data?.savePoseFromMaster?.error) showToast(res.data.savePoseFromMaster.message, 'error');
      else { showToast('Pose saved'); refetch(); }
    } catch (err) {
      showToast(err.message || 'Save failed', 'error');
    } finally {
      setSavingMasterId(null);
    }
  };

  if (loading || masterLoading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />

      <div className="settings-section-header">
        <h3 className="settings-section-title">Poses</h3>
        <button className="btn btn--primary" onClick={() => setShowSelector(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Pose
        </button>
      </div>

      <AddDrawer
        open={showSelector}
        onClose={() => { setShowSelector(false); setSearch(''); }}
        title="Add Pose"
        subtitle="Pick from the master library"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search poses…"
      >
        {unsavedMasterPoses.length === 0 ? (
          <EmptyDrawerMessage>No master poses available to add.</EmptyDrawerMessage>
        ) : filteredUnsavedMasterPoses.length === 0 ? (
          <EmptyDrawerMessage>No poses match "{search}".</EmptyDrawerMessage>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredUnsavedMasterPoses.map((mp) => (
              <div key={mp.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.6rem 0.75rem',
              }}>
                {mp.imageUrl ? (
                  <img src={mp.imageUrl} alt={mp.name}
                    style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', flexShrink: 0 }}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div style={{ width: 44, height: 44, borderRadius: 6, background: '#f1f5f9', border: '1px solid #e2e8f0', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: '0.9rem', color: '#1e293b', display: 'block' }}>{mp.name}</strong>
                  {(mp.framing || mp.angle) && (
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                      {[mp.framing, mp.angle].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <button
                  className="btn btn--primary"
                  style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem', flexShrink: 0 }}
                  onClick={() => handleSaveFromMaster(mp)}
                  disabled={savingMasterId === mp.id}
                >
                  {savingMasterId === mp.id ? 'Saving…' : 'Add'}
                </button>
              </div>
            ))}
          </div>
        )}
      </AddDrawer>

      {poses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h7" />
            </svg>
          </div>
          <p className="empty-state-title">No poses</p>
          <p className="empty-state-desc">
            {unsavedMasterPoses.length > 0 ? 'Pick from the master library to get started' : 'No master poses are available yet'}
          </p>
        </div>
      ) : (
        <div className="dim-list">
          {poses.map((p) => (
            <div key={p.id} className="dim-item">
              <div className="dim-item-info" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.name}
                    style={{ width: '2.5rem', height: '2.5rem', objectFit: 'cover', borderRadius: '0.375rem', flexShrink: 0, border: '1px solid var(--slate-200)' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.375rem', background: 'var(--color-bg-tertiary, #f0f0f0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid var(--slate-200)' }}>
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width={16} height={16} style={{ color: 'var(--color-text-secondary, #888)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <span className="dim-item-name" style={{ textAlign: 'left' }}>{p.name}</span>
              </div>
              <button className="row-btn row-btn--delete" onClick={() => handleDelete(p.id)}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Model Expression Tab ───────────────────────────────────────
function ModelExpressionTab() {
  const [toast, showToast] = useToast();
  const [showSelector, setShowSelector] = useState(false);
  const [savingMasterId, setSavingMasterId] = useState(null);
  const [search, setSearch] = useState('');

  const { data, loading, refetch } = useQuery(GET_MODEL_EXPRESSION_LISTS, { fetchPolicy: 'network-only' });
  const { data: masterData, loading: masterLoading } = useQuery(GET_MASTER_MODEL_EXPRESSIONS_FOR_CLIENT, { fetchPolicy: 'network-only' });
  const [deleteModelExpressionList] = useMutation(DELETE_MODEL_EXPRESSION_LIST);
  const [saveModelExpressionFromMaster] = useMutation(SAVE_MODEL_EXPRESSION_FROM_MASTER);

  const items = data?.getModelExpressionLists?.modelExpressionLists || [];
  const masterItems = masterData?.getMasterModelExpressionsForClient?.masterModelExpressions || [];
  const savedMasterIds = items.filter((p) => p.masterModelExpressionId).map((p) => p.masterModelExpressionId);
  const unsavedMasterItems = masterItems.filter((mp) => !savedMasterIds.includes(mp.id));
  const q = search.trim().toLowerCase();
  const filteredUnsavedMasterItems = q
    ? unsavedMasterItems.filter((mp) =>
        (mp.name || '').toLowerCase().includes(q) || (mp.expression || '').toLowerCase().includes(q))
    : unsavedMasterItems;

  const handleDelete = async (id) => {
    const res = await deleteModelExpressionList({ variables: { id } });
    if (res.data?.deleteModelExpressionList?.error) showToast(res.data.deleteModelExpressionList.message, 'error');
    else { showToast('Removed'); refetch(); }
  };

  const handleSaveFromMaster = async (mp) => {
    setSavingMasterId(mp.id);
    try {
      const input = {
        name: mp.name,
        expression: mp.expression || '',
      };
      const res = await saveModelExpressionFromMaster({ variables: { masterModelExpressionId: mp.id, input } });
      if (res.data?.saveModelExpressionFromMaster?.error) showToast(res.data.saveModelExpressionFromMaster.message, 'error');
      else { showToast('Model expression saved'); refetch(); }
    } catch (err) {
      showToast(err.message || 'Save failed', 'error');
    } finally {
      setSavingMasterId(null);
    }
  };

  if (loading || masterLoading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />

      <div className="settings-section-header">
        <h3 className="settings-section-title">Model Expression</h3>
        <button className="btn btn--primary" onClick={() => setShowSelector(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Expression
        </button>
      </div>

      <AddDrawer
        open={showSelector}
        onClose={() => { setShowSelector(false); setSearch(''); }}
        title="Add Model Expression"
        subtitle="Pick from the master library"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search expressions…"
      >
        {unsavedMasterItems.length === 0 ? (
          <EmptyDrawerMessage>No master model expressions available to add.</EmptyDrawerMessage>
        ) : filteredUnsavedMasterItems.length === 0 ? (
          <EmptyDrawerMessage>No expressions match "{search}".</EmptyDrawerMessage>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredUnsavedMasterItems.map((mp) => (
              <div key={mp.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.7rem 0.85rem',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: '0.9rem', color: '#1e293b', display: 'block' }}>{mp.name}</strong>
                  <p style={{
                    margin: '2px 0 0 0', fontSize: '0.78rem', color: '#64748b',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }} title={mp.expression || ''}>
                    {mp.expression || '—'}
                  </p>
                </div>
                <button
                  className="btn btn--primary"
                  style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem', flexShrink: 0 }}
                  onClick={() => handleSaveFromMaster(mp)}
                  disabled={savingMasterId === mp.id}
                >
                  {savingMasterId === mp.id ? 'Saving…' : 'Add'}
                </button>
              </div>
            ))}
          </div>
        )}
      </AddDrawer>

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h7" />
            </svg>
          </div>
          <p className="empty-state-title">No model expressions</p>
          <p className="empty-state-desc">
            {unsavedMasterItems.length > 0 ? 'Pick from the master library to get started' : 'No master model expressions are available yet'}
          </p>
        </div>
      ) : (
        <div className="dim-list">
          {items.map((p) => (
            <div key={p.id} className="dim-item">
              <div className="dim-item-info" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem', flex: 1, minWidth: 0 }}>
                <span className="dim-item-name" style={{ textAlign: 'left' }}>{p.name}</span>
                <span style={{ fontSize: '0.8rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }} title={p.expression || ''}>
                  {p.expression || '—'}
                </span>
              </div>
              <button className="row-btn row-btn--delete" onClick={() => handleDelete(p.id)}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Description Template Tab ───────────────────────────────────
// Admin-side: save templates from the master library + delete. The active
// template per staff is selected from the mobile app — admin doesn't have
// a per-staff context, so no "Use" toggle here.
function DescriptionTemplateTab() {
  const [toast, showToast] = useToast();
  const [showSelector, setShowSelector] = useState(false);
  const [savingMasterId, setSavingMasterId] = useState(null);
  const [search, setSearch] = useState('');
  // Configure modal state — opens when the admin clicks Add (on a master row)
  // or Edit (on a saved store row). Holds the master template being configured
  // and a Set of section labels the admin has chosen.
  //   mode: 'add' | 'edit'
  const [configuring, setConfiguring] = useState(null);
  const [pickedLabels, setPickedLabels] = useState(new Set());

  const { data, loading, refetch } = useQuery(GET_DESCRIPTION_TEMPLATES, { fetchPolicy: 'network-only' });
  const { data: masterData, loading: masterLoading } = useQuery(GET_MASTER_DESCRIPTION_TEMPLATES_FOR_CLIENT, { fetchPolicy: 'network-only' });
  const [deleteTemplate] = useMutation(DELETE_DESCRIPTION_TEMPLATE);
  const [saveFromMaster] = useMutation(SAVE_DESCRIPTION_TEMPLATE_FROM_MASTER);
  const [updateTemplate] = useMutation(UPDATE_DESCRIPTION_TEMPLATE);

  const items = data?.getDescriptionTemplates?.descriptionTemplates || [];

  const masterItems = masterData?.getMasterDescriptionTemplatesForClient?.masterDescriptionTemplates || [];
  const savedMasterIds = items.filter((p) => p.masterDescriptionTemplateId).map((p) => p.masterDescriptionTemplateId);
  const unsavedMasterItems = masterItems.filter((m) => !savedMasterIds.includes(m.id));
  const q = search.trim().toLowerCase();
  const filteredUnsaved = q
    ? unsavedMasterItems.filter((m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.format || '').toLowerCase().includes(q) ||
        (m.tone || '').toLowerCase().includes(q))
    : unsavedMasterItems;

  const masterHasSections = (m) =>
    (m?.format === 'sections' || m?.format === 'bullets') && Array.isArray(m?.sections) && m.sections.length > 0;

  const summary = (t) => {
    const bits = [t.format, t.tone, `${t.maxWords || 80} words`].filter(Boolean);
    if ((t.format === 'sections' || t.format === 'bullets') && t.sections?.length) {
      bits.push(`${t.sections.length} section${t.sections.length === 1 ? '' : 's'}`);
    }
    return bits.join(' · ');
  };

  const handleDelete = async (id) => {
    const res = await deleteTemplate({ variables: { id } });
    if (res.data?.deleteDescriptionTemplate?.error) {
      showToast(res.data.deleteDescriptionTemplate.message, 'error');
    } else {
      showToast('Removed');
      refetch();
    }
  };

  // Builds the input payload for save/update, picking only sections whose
  // labels are in `labelSet`.
  const buildInput = (master, labelSet) => ({
    name: master.name,
    format: master.format,
    tone: master.tone || '',
    maxWords: master.maxWords || 80,
    sections: (master.sections || [])
      .filter((s) => labelSet.has(s.label))
      .map((s) => ({
        label: s.label,
        include: s.include || '',
        maxWords: s.maxWords || 25,
      })),
  });

  // Save the master directly when there's nothing to configure (no sections
  // in the template). Used for paragraph/auto formats.
  const directSaveAll = async (m) => {
    setSavingMasterId(m.id);
    try {
      const input = {
        name: m.name,
        format: m.format,
        tone: m.tone || '',
        maxWords: m.maxWords || 80,
        sections: [],
      };
      const res = await saveFromMaster({ variables: { masterDescriptionTemplateId: m.id, input } });
      if (res.data?.saveDescriptionTemplateFromMaster?.error) {
        showToast(res.data.saveDescriptionTemplateFromMaster.message, 'error');
      } else {
        showToast('Template saved');
        refetch();
        setShowSelector(false);
      }
    } catch (err) {
      showToast(err.message || 'Save failed', 'error');
    } finally {
      setSavingMasterId(null);
    }
  };

  const openConfigureForAdd = (m) => {
    if (!masterHasSections(m)) {
      directSaveAll(m);
      return;
    }
    setConfiguring({ mode: 'add', master: m, savedId: null });
    // Default: pre-select every section.
    setPickedLabels(new Set(m.sections.map((s) => s.label)));
  };

  const openConfigureForEdit = (saved) => {
    const master = masterItems.find((m) => m.id === saved.masterDescriptionTemplateId);
    if (!master) {
      showToast('Original master template is no longer available', 'error');
      return;
    }
    setConfiguring({ mode: 'edit', master, savedId: saved.id });
    // Pre-check the labels already saved on the store template.
    setPickedLabels(new Set((saved.sections || []).map((s) => s.label)));
  };

  const closeConfigure = () => {
    setConfiguring(null);
    setPickedLabels(new Set());
  };

  const toggleSection = (label) => {
    setPickedLabels((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleConfirmConfigure = async () => {
    if (!configuring) return;
    const { mode, master, savedId } = configuring;
    if (pickedLabels.size === 0) {
      showToast('Pick at least one section', 'error');
      return;
    }
    setSavingMasterId(master.id);
    try {
      const input = buildInput(master, pickedLabels);
      if (mode === 'edit' && savedId) {
        const res = await updateTemplate({ variables: { id: savedId, input } });
        if (res.data?.updateDescriptionTemplate?.error) {
          showToast(res.data.updateDescriptionTemplate.message, 'error');
        } else {
          showToast('Template updated');
          refetch();
          closeConfigure();
        }
      } else {
        const res = await saveFromMaster({ variables: { masterDescriptionTemplateId: master.id, input } });
        if (res.data?.saveDescriptionTemplateFromMaster?.error) {
          showToast(res.data.saveDescriptionTemplateFromMaster.message, 'error');
        } else {
          showToast('Template saved');
          refetch();
          closeConfigure();
          setShowSelector(false);
        }
      }
    } catch (err) {
      showToast(err.message || 'Save failed', 'error');
    } finally {
      setSavingMasterId(null);
    }
  };

  if (loading || masterLoading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />

      <div className="settings-section-header">
        <h3 className="settings-section-title">Description Templates</h3>
        <button className="btn btn--primary" onClick={() => setShowSelector(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Template
        </button>
      </div>

      <AddDrawer
        open={showSelector}
        onClose={() => { setShowSelector(false); setSearch(''); }}
        title="Add Description Template"
        subtitle="Pick from the master library"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search templates…"
      >
        {unsavedMasterItems.length === 0 ? (
          <EmptyDrawerMessage>No master templates available to add.</EmptyDrawerMessage>
        ) : filteredUnsaved.length === 0 ? (
          <EmptyDrawerMessage>No templates match "{search}".</EmptyDrawerMessage>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredUnsaved.map((m) => (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.6rem 0.75rem',
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 6,
                  background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#6366f1', flexShrink: 0,
                }}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 22, height: 22 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M4 6h16M4 10h10M4 14h16M4 18h10" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: '0.9rem', color: '#1e293b', display: 'block' }}>{m.name}</strong>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{summary(m)}</div>
                </div>
                <button
                  className="btn btn--primary"
                  style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem', flexShrink: 0 }}
                  onClick={() => openConfigureForAdd(m)}
                  disabled={savingMasterId === m.id}
                >
                  {savingMasterId === m.id
                    ? 'Saving…'
                    : masterHasSections(m) ? 'Configure' : 'Add'}
                </button>
              </div>
            ))}
          </div>
        )}
      </AddDrawer>

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h10M4 14h16M4 18h10" />
            </svg>
          </div>
          <p className="empty-state-title">No description templates</p>
          <p className="empty-state-desc">
            {unsavedMasterItems.length > 0 ? 'Pick from the master library to get started.' : 'No master templates are available yet.'}
          </p>
        </div>
      ) : (
        <div className="dim-list">
          {items.map((t) => {
            const sourceMaster = t.masterDescriptionTemplateId
              ? masterItems.find((m) => m.id === t.masterDescriptionTemplateId)
              : null;
            const canEditSections = sourceMaster && masterHasSections(sourceMaster);
            return (
              <div key={t.id} className="dim-item">
                <div className="dim-item-info" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: '2.5rem', height: '2.5rem', borderRadius: '0.375rem',
                    background: 'var(--color-bg-tertiary, #f1f5f9)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    color: '#64748b',
                  }}>
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 18, height: 18 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M4 6h16M4 10h10M4 14h16M4 18h10" />
                    </svg>
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <span className="dim-item-name" style={{ textAlign: 'left', display: 'block' }}>{t.name}</span>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {summary(t)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {canEditSections && (
                    <button
                      type="button"
                      onClick={() => openConfigureForEdit(t)}
                      style={{
                        background: 'transparent', border: '1px solid #cbd5e1',
                        color: '#475569', borderRadius: '0.4rem',
                        padding: '0.3rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer',
                      }}
                    >Edit sections</button>
                  )}
                  <button className="row-btn row-btn--delete" onClick={() => handleDelete(t.id)}>
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {configuring && (
        <ConfigureSectionsModal
          mode={configuring.mode}
          master={configuring.master}
          pickedLabels={pickedLabels}
          onToggle={toggleSection}
          onSelectAll={() => setPickedLabels(new Set(configuring.master.sections.map((s) => s.label)))}
          onClearAll={() => setPickedLabels(new Set())}
          onClose={closeConfigure}
          onConfirm={handleConfirmConfigure}
          saving={savingMasterId === configuring.master.id}
        />
      )}
    </div>
  );
}

// ── Section picker modal ───────────────────────────────────────
// Shown when the admin clicks "Configure" while adding from master, or
// "Edit sections" on an already-saved store template. Lets the admin
// toggle which master sections are enabled for this store.
function ConfigureSectionsModal({
  mode, master, pickedLabels, onToggle, onSelectAll, onClearAll, onClose, onConfirm, saving,
}) {
  const sections = master.sections || [];
  const totalPicked = pickedLabels.size;
  const allPicked = totalPicked === sections.length && sections.length > 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'edit' ? 'Edit sections' : 'Configure sections'} — {master.name}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '0 0 0.5rem 0' }}>
            Tick the sections you want the AI to include for this store.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
              {totalPicked} of {sections.length} selected
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                type="button"
                onClick={onSelectAll}
                disabled={allPicked}
                style={{
                  background: 'transparent', border: 'none', cursor: allPicked ? 'not-allowed' : 'pointer',
                  color: allPicked ? '#cbd5e1' : '#6366f1', fontSize: '0.75rem', padding: '0.25rem 0.4rem',
                }}
              >Select all</button>
              <button
                type="button"
                onClick={onClearAll}
                disabled={totalPicked === 0}
                style={{
                  background: 'transparent', border: 'none', cursor: totalPicked === 0 ? 'not-allowed' : 'pointer',
                  color: totalPicked === 0 ? '#cbd5e1' : '#f43f5e', fontSize: '0.75rem', padding: '0.25rem 0.4rem',
                }}
              >Clear all</button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '320px', overflowY: 'auto' }}>
            {sections.map((s) => {
              const checked = pickedLabels.has(s.label);
              return (
                <label
                  key={s.label}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                    padding: '0.6rem 0.75rem',
                    border: checked ? '2px solid #6366f1' : '1px solid #e2e8f0',
                    borderRadius: '0.5rem', cursor: 'pointer',
                    background: checked ? '#6366f108' : '#fff',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(s.label)}
                    style={{ accentColor: '#6366f1', marginTop: 3 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{s.label}</div>
                    {s.include && (
                      <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
                        {s.include}
                      </div>
                    )}
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>
                      Max {s.maxWords || 25} words
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={onConfirm}
              disabled={saving || totalPicked === 0}
            >
              {saving ? 'Saving…' : mode === 'edit' ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Background Tab ─────────────────────────────────────────────
function BackgroundTab() {
  const [toast, showToast] = useToast();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'color', color: '#ffffff', file: null, preview: null });
  const [uploading, setUploading] = useState(false);

  const { data, loading, refetch } = useQuery(GET_BACKGROUNDS, { fetchPolicy: 'network-only' });
  const [addBackground] = useMutation(ADD_BACKGROUND);
  const [deleteBackground] = useMutation(DELETE_BACKGROUND);

  const backgrounds = data?.getBackgrounds?.backgrounds || [];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setForm((f) => ({ ...f, file, preview }));
  };

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setUploading(true);
    try {
      let imageUrl = null;
      let color = null;
      if (form.type === 'image') {
        if (!form.file) { showToast('Please select an image', 'error'); setUploading(false); return; }
        const res = await uploadImage(form.file, 'background');
        imageUrl = res.url;
      } else {
        color = form.color;
      }
      const res = await addBackground({ variables: { name: form.name.trim(), imageUrl, color } });
      if (res.data?.addBackground?.error) showToast(res.data.addBackground.message, 'error');
      else {
        showToast('Background added');
        if (form.preview) URL.revokeObjectURL(form.preview);
        setForm({ name: '', type: 'color', color: '#ffffff', file: null, preview: null });
        setAdding(false);
        refetch();
      }
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    const res = await deleteBackground({ variables: { id } });
    if (res.data?.deleteBackground?.error) showToast(res.data.deleteBackground.message, 'error');
    else { showToast('Deleted'); refetch(); }
  };

  const cancelAdd = () => {
    if (form.preview) URL.revokeObjectURL(form.preview);
    setForm({ name: '', type: 'color', color: '#ffffff', file: null, preview: null });
    setAdding(false);
  };

  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />
      <div className="settings-section-header">
        <h3 className="settings-section-title">Backgrounds</h3>
        <button className="btn btn--primary" onClick={() => setAdding(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Background
        </button>
      </div>

      {adding && (
        <div className="bg-add-form">
          <input
            className="form-input"
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="bg-type-toggle">
            <button
              className={`btn ${form.type === 'color' ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setForm({ ...form, type: 'color', file: null, preview: null })}
            >
              Color
            </button>
            <button
              className={`btn ${form.type === 'image' ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setForm({ ...form, type: 'image' })}
            >
              Image
            </button>
          </div>
          {form.type === 'color' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                style={{ width: '2.5rem', height: '2.5rem', border: 'none', padding: 0, cursor: 'pointer', borderRadius: '0.375rem' }}
              />
              <span style={{ fontSize: '0.875rem', color: 'var(--slate-400)' }}>{form.color}</span>
            </div>
          ) : (
            <div className="bg-upload-area">
              {form.preview ? (
                <img src={form.preview} alt="preview" className="bg-upload-preview" />
              ) : (
                <label className="bg-upload-label">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width={24} height={24}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Click to upload image</span>
                  <input type="file" accept="image/jpeg,image/png,image/jpg,image/webp" onChange={handleFileChange} style={{ display: 'none' }} />
                </label>
              )}
              {form.preview && (
                <button className="btn btn--ghost" style={{ fontSize: '0.75rem' }} onClick={() => { URL.revokeObjectURL(form.preview); setForm({ ...form, file: null, preview: null }); }}>
                  Remove
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn--primary" onClick={handleAdd} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Add'}
            </button>
            <button className="btn btn--ghost" onClick={cancelAdd}>Cancel</button>
          </div>
        </div>
      )}

      {backgrounds.length === 0 && !adding ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="empty-state-title">No backgrounds</p>
          <p className="empty-state-desc">Add a background image or color</p>
        </div>
      ) : (
        <div className="dim-list">
          {backgrounds.map((bg) => (
            <div key={bg.id} className="dim-item">
              <div className="dim-item-info" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {bg.imageUrl ? (
                  <img src={bg.imageUrl} alt={bg.name} style={{ width: '2.5rem', height: '2.5rem', objectFit: 'cover', borderRadius: '0.375rem', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.375rem', background: bg.color || '#ccc', flexShrink: 0, border: '1px solid var(--slate-700)' }} />
                )}
                <span className="dim-item-name">{bg.name}</span>
              </div>
              <div className="dim-item-actions">
                <button className="row-btn row-btn--delete" onClick={() => handleDelete(bg.id)}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Lighting Tab ───────────────────────────────────────────────
function LightingTab() {
  const [toast, showToast] = useToast();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  const { data, loading, refetch } = useQuery(GET_LIGHTINGS, { fetchPolicy: 'network-only' });
  const [addLighting, { loading: creating }] = useMutation(ADD_LIGHTING);
  const [deleteLighting] = useMutation(DELETE_LIGHTING);

  const lightings = data?.getLightings?.lightings || [];

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const res = await addLighting({ variables: { name: newName.trim() } });
    if (res.data?.addLighting?.error) showToast(res.data.addLighting.message, 'error');
    else { showToast('Lighting added'); setNewName(''); setAdding(false); refetch(); }
  };

  const handleDelete = async (id) => {
    const res = await deleteLighting({ variables: { id } });
    if (res.data?.deleteLighting?.error) showToast(res.data.deleteLighting.message, 'error');
    else { showToast('Deleted'); refetch(); }
  };

  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />
      <div className="settings-section-header">
        <h3 className="settings-section-title">Lightings</h3>
        <button className="btn btn--primary" onClick={() => setAdding(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Lighting
        </button>
      </div>

      {adding && (
        <div className="settings-inline-form">
          <input
            className="form-input"
            placeholder="Lighting name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
          <button className="btn btn--primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Adding...' : 'Add'}
          </button>
          <button className="btn btn--ghost" onClick={() => { setAdding(false); setNewName(''); }}>Cancel</button>
        </div>
      )}

      {lightings.length === 0 && !adding ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <p className="empty-state-title">No lightings</p>
          <p className="empty-state-desc">Add a lighting option to get started</p>
        </div>
      ) : (
        <div className="dim-list">
          {lightings.map((lt) => (
            <div key={lt.id} className="dim-item">
              <div className="dim-item-info">
                <span className="dim-item-name">{lt.name}</span>
              </div>
              <div className="dim-item-actions">
                <button className="row-btn row-btn--delete" onClick={() => handleDelete(lt.id)}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Dimensions Tab ─────────────────────────────────────────────
function DimensionsTab() {
  const [toast, showToast] = useToast();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', width: '', height: '' });

  const { data, loading, refetch } = useQuery(GET_DIMENSIONS, { fetchPolicy: 'network-only' });
  const [createDimension, { loading: creating }] = useMutation(CREATE_DIMENSION);
  const [setActiveDimension] = useMutation(SET_ACTIVE_DIMENSION);
  const [deleteDimension] = useMutation(DELETE_DIMENSION);

  const dimensions = data?.getDimensions?.dimensions || [];

  const handleCreate = async () => {
    if (!form.name.trim() || !form.width || !form.height) return;
    const res = await createDimension({
      variables: { name: form.name.trim(), width: parseFloat(form.width), height: parseFloat(form.height) },
    });
    if (res.data?.createDimension?.error) showToast(res.data.createDimension.message, 'error');
    else { showToast('Dimension created'); setForm({ name: '', width: '', height: '' }); setAdding(false); refetch(); }
  };

  const handleSetActive = async (id) => {
    await setActiveDimension({ variables: { id } });
    showToast('Active dimension updated');
    refetch();
  };

  const handleDelete = async (id) => {
    const res = await deleteDimension({ variables: { id } });
    if (res.data?.deleteDimension?.error) showToast(res.data.deleteDimension.message, 'error');
    else { showToast('Deleted'); refetch(); }
  };

  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />
      <div className="settings-section-header">
        <h3 className="settings-section-title">Dimensions</h3>
        <button className="btn btn--primary" onClick={() => setAdding(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add
        </button>
      </div>

      {adding && (
        <div className="settings-inline-form">
          <input className="form-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="form-input" placeholder="Width" type="number" value={form.width} onChange={(e) => setForm({ ...form, width: e.target.value })} />
          <input className="form-input" placeholder="Height" type="number" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} />
          <button className="btn btn--primary" onClick={handleCreate} disabled={creating}>{creating ? 'Creating...' : 'Create'}</button>
          <button className="btn btn--ghost" onClick={() => { setAdding(false); setForm({ name: '', width: '', height: '' }); }}>Cancel</button>
        </div>
      )}

      {dimensions.length === 0 && !adding ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </div>
          <p className="empty-state-title">No dimensions</p>
          <p className="empty-state-desc">Add your first dimension</p>
        </div>
      ) : (
        <div className="dim-list">
          {dimensions.map((d) => (
            <div key={d.id} className={`dim-item ${d.active ? 'dim-item--active' : ''}`}>
              <div className="dim-item-info">
                <span className="dim-item-name">{d.name}</span>
                <span className="dim-item-size">{d.width} × {d.height}</span>
              </div>
              <div className="dim-item-actions">
                {d.active ? (
                  <span className="dim-active-badge">Active</span>
                ) : (
                  <button className="btn btn--ghost" style={{ fontSize: '0.75rem', padding: '0.3rem 0.7rem' }} onClick={() => handleSetActive(d.id)}>
                    Set Active
                  </button>
                )}
                <button className="row-btn row-btn--delete" onClick={() => handleDelete(d.id)}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Logo Tab ───────────────────────────────────────────────────
function LogoTab() {
  const [toast, showToast] = useToast();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', file: null, preview: null });
  const [uploading, setUploading] = useState(false);

  const { data, loading, refetch } = useQuery(GET_LOGO_SETTING, { fetchPolicy: 'network-only' });
  const [addLogo] = useMutation(ADD_LOGO);
  const [deleteLogo] = useMutation(DELETE_LOGO);

  const logos = data?.getLogoSetting?.logos || [];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setForm((f) => ({ ...f, file, preview }));
  };

  const handleAdd = async () => {
    if (!form.name.trim() || !form.file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(form.file, 'logo');
      const res = await addLogo({ variables: { name: form.name.trim(), url } });
      if (res.data?.addLogo?.error) showToast(res.data.addLogo.message, 'error');
      else {
        showToast('Logo added');
        if (form.preview) URL.revokeObjectURL(form.preview);
        setForm({ name: '', file: null, preview: null });
        setAdding(false);
        refetch();
      }
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    const res = await deleteLogo({ variables: { id } });
    if (res.data?.deleteLogo?.error) showToast(res.data.deleteLogo.message, 'error');
    else { showToast('Logo deleted'); refetch(); }
  };

  const cancelAdd = () => {
    if (form.preview) URL.revokeObjectURL(form.preview);
    setForm({ name: '', file: null, preview: null });
    setAdding(false);
  };

  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />
      <div className="settings-section-header">
        <h3 className="settings-section-title">Logos</h3>
        <button className="btn btn--primary" onClick={() => setAdding(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Logo
        </button>
      </div>

      {adding && (
        <div className="bg-add-form">
          <input
            className="form-input"
            placeholder="Logo name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="bg-upload-area">
            {form.preview ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <img src={form.preview} alt="preview" style={{ width: '3rem', height: '3rem', objectFit: 'contain', borderRadius: '0.375rem', border: '1px solid var(--slate-700)' }} />
                <span style={{ fontSize: '0.875rem', color: 'var(--slate-300)', flex: 1 }}>{form.file?.name}</span>
                <button className="btn btn--ghost" style={{ fontSize: '0.75rem' }} onClick={() => { URL.revokeObjectURL(form.preview); setForm({ ...form, file: null, preview: null }); }}>Remove</button>
              </div>
            ) : (
              <label className="bg-upload-label">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width={24} height={24}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Click to upload logo image</span>
                <input type="file" accept="image/jpeg,image/png,image/jpg,image/webp" onChange={handleFileChange} style={{ display: 'none' }} />
              </label>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn--primary" onClick={handleAdd} disabled={uploading || !form.name.trim() || !form.file}>
              {uploading ? 'Uploading...' : 'Add'}
            </button>
            <button className="btn btn--ghost" onClick={cancelAdd}>Cancel</button>
          </div>
        </div>
      )}

      {logos.length === 0 && !adding ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="empty-state-title">No logos</p>
          <p className="empty-state-desc">Upload a logo image to get started</p>
        </div>
      ) : (
        <div className="dim-list">
          {logos.map((logo) => (
            <div key={logo.id} className="dim-item">
              <div className="dim-item-info" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <img src={logo.url} alt={logo.name} style={{ width: '2.5rem', height: '2.5rem', objectFit: 'contain', borderRadius: '0.375rem', border: '1px solid var(--slate-700)', flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
                <span className="dim-item-name">{logo.name}</span>
              </div>
              <div className="dim-item-actions">
                <button className="row-btn row-btn--delete" onClick={() => handleDelete(logo.id)}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Position Tab ───────────────────────────────────────────────
function PositionTab() {
  const [toast, showToast] = useToast();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  const { data, loading, refetch } = useQuery(GET_POSITIONS, { fetchPolicy: 'network-only' });
  const [addPosition, { loading: creating }] = useMutation(ADD_POSITION);
  const [deletePosition] = useMutation(DELETE_POSITION);

  const positions = data?.getPositions?.positions || [];

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const res = await addPosition({ variables: { name: newName.trim() } });
    if (res.data?.addPosition?.error) showToast(res.data.addPosition.message, 'error');
    else { showToast('Position added'); setNewName(''); setAdding(false); refetch(); }
  };

  const handleDelete = async (id) => {
    const res = await deletePosition({ variables: { id } });
    if (res.data?.deletePosition?.error) showToast(res.data.deletePosition.message, 'error');
    else { showToast('Deleted'); refetch(); }
  };

  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />
      <div className="settings-section-header">
        <h3 className="settings-section-title">Positions</h3>
        <button className="btn btn--primary" onClick={() => setAdding(true)}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Position
        </button>
      </div>

      {adding && (
        <div className="settings-inline-form">
          <input
            className="form-input"
            placeholder="Position name (e.g. top_left)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
          <button className="btn btn--primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Adding...' : 'Add'}
          </button>
          <button className="btn btn--ghost" onClick={() => { setAdding(false); setNewName(''); }}>Cancel</button>
        </div>
      )}

      {positions.length === 0 && !adding ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </div>
          <p className="empty-state-title">No positions</p>
          <p className="empty-state-desc">Add a position option to get started</p>
        </div>
      ) : (
        <div className="dim-list">
          {positions.map((p) => (
            <div key={p.id} className="dim-item">
              <div className="dim-item-info">
                <span className="dim-item-name">{p.name}</span>
              </div>
              <div className="dim-item-actions">
                <button className="row-btn row-btn--delete" onClick={() => handleDelete(p.id)}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Text Tab ───────────────────────────────────────────────────
function TextTab() {
  const [toast, showToast] = useToast();
  const { data, loading, refetch } = useQuery(GET_TEXT_SETTING, { fetchPolicy: 'network-only' });
  const [updateText] = useMutation(UPDATE_TEXT_SETTING);

  const enabled = data?.getTextSetting?.setting?.enabled ?? false;

  const handleToggle = async () => {
    const next = !enabled;
    await updateText({ variables: { enabled: next } });
    showToast(`Text overlay ${next ? 'enabled' : 'disabled'}`);
    refetch();
  };

  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />

      <div className="settings-toggle-row">
        <div>
          <h3 className="settings-section-title">Text Overlay</h3>
          <p style={{ fontSize: '0.8125rem', color: 'var(--slate-400)', marginTop: '2px' }}>
            Enable or disable text overlay on the model render
          </p>
        </div>
        <button className={`toggle-btn ${enabled ? 'toggle-btn--on' : ''}`} onClick={handleToggle}>
          <span className="toggle-knob" />
        </button>
      </div>
    </div>
  );
}

// ── Preference Tab ─────────────────────────────────────────────
function PreferenceTab() {
  const [toast, showToast] = useToast();
  const [selectedAttrId, setSelectedAttrId] = useState('');
  const [selectedValues, setSelectedValues] = useState([]);
  const [manualName, setManualName] = useState('');
  const [manualValueInput, setManualValueInput] = useState('');

  const isShopify = ['shopify', 'etailon'].includes(sessionStorage.getItem('websiteType'));

  const { data: attrsData, loading: attrsLoading } = useQuery(GET_ATTRIBUTES, { fetchPolicy: 'network-only', skip: isShopify });
  const { loading: savedLoading } = useQuery(GET_PREFERENCE_VARIANT, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const s = res?.getPreferenceVariant?.setting;
      if (s?.attributeId) {
        if (isShopify) {
          setManualName(s.attributeName || s.attributeId || '');
        } else {
          setSelectedAttrId(s.attributeId);
        }
        setSelectedValues(s.fieldValues?.map((fv) => ({ id: fv.id, value: fv.value })) || []);
      }
    },
  });
  const [save, { loading: saving }] = useMutation(SAVE_PREFERENCE_VARIANT);

  const attributes = attrsData?.getAttributes?.attributes || [];
  const activeAttr = attributes.find((a) => a.id === selectedAttrId);

  const handleAttrChange = (attrId) => {
    setSelectedAttrId(attrId);
    setSelectedValues([]);
  };

  const toggleFieldValue = (fv) => {
    setSelectedValues((prev) => {
      const exists = prev.find((v) => v.id === fv.id);
      return exists ? prev.filter((v) => v.id !== fv.id) : [...prev, { id: fv.id, value: fv.value }];
    });
  };

  const addManualValue = () => {
    const val = manualValueInput.trim();
    if (!val) return;
    if (selectedValues.find((v) => v.value === val)) return showToast('Value already exists', 'error');
    setSelectedValues((prev) => [...prev, { id: val, value: val }]);
    setManualValueInput('');
  };

  const removeManualValue = (id) => {
    setSelectedValues((prev) => prev.filter((v) => v.id !== id));
  };

  const handleSave = async () => {
    if (isShopify) {
      if (!manualName.trim()) return showToast('Enter a name first', 'error');
      const name = manualName.trim();
      const res = await save({
        variables: {
          attributeId: name,
          attributeName: name,
          fieldValues: selectedValues,
        },
      });
      if (res?.data?.savePreferenceVariant?.error) {
        showToast(res.data.savePreferenceVariant.message || 'Failed to save', 'error');
      } else {
        showToast('Preference variant saved');
      }
    } else {
      if (!selectedAttrId) return showToast('Select an attribute first', 'error');
      const attr = attributes.find((a) => a.id === selectedAttrId);
      const res = await save({
        variables: {
          attributeId: selectedAttrId,
          attributeName: attr?.labelName || '',
          fieldValues: selectedValues,
        },
      });
      if (res?.data?.savePreferenceVariant?.error) {
        showToast(res.data.savePreferenceVariant.message || 'Failed to save', 'error');
      } else {
        showToast('Preference variant saved');
      }
    }
  };

  if ((!isShopify && attrsLoading) || savedLoading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />

      <div className="pref-header">
        <h3 className="settings-section-title">Preference Variant</h3>
        <p className="settings-section-desc">
          {isShopify
            ? 'Enter a preference name and add values to use as variant preferences.'
            : 'Select an attribute and choose which values to use as variant preferences.'}
        </p>
      </div>

      {isShopify ? (
        <>
          <div className="pref-field">
            <label className="pref-label">Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Size, Color"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
            />
          </div>

          <div className="pref-field">
            <label className="pref-label">Field Values</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Enter a value"
                value={manualValueInput}
                onChange={(e) => setManualValueInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addManualValue()}
              />
              <button className="btn btn--primary" onClick={addManualValue} type="button">Add</button>
            </div>
            {selectedValues.length > 0 && (
              <div className="pref-values-list">
                {selectedValues.map((fv) => (
                  <span key={fv.id} className="pref-value-item pref-value-item--checked" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {fv.value}
                    <button
                      type="button"
                      onClick={() => removeManualValue(fv.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px', lineHeight: 1 }}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="pref-field">
            <label className="pref-label">Attribute</label>
            <select
              className="pref-select"
              value={selectedAttrId}
              onChange={(e) => handleAttrChange(e.target.value)}
            >
              <option value="">-- Select an attribute --</option>
              {attributes.map((attr) => (
                <option key={attr.id} value={attr.id}>{attr.labelName}</option>
              ))}
            </select>
          </div>

          {activeAttr && (
            <div className="pref-field">
              <label className="pref-label">Field Values <span className="pref-label-hint">(multi-select)</span></label>
              <div className="pref-values-list">
                {activeAttr.fieldValues?.map((fv) => {
                  const checked = !!selectedValues.find((v) => v.id === fv.id);
                  return (
                    <label key={fv.id} className={`pref-value-item ${checked ? 'pref-value-item--checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFieldValue(fv)}
                        className="pref-checkbox"
                      />
                      {fv.value}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      <button
        className="btn btn--primary"
        style={{ alignSelf: 'flex-start' }}
        onClick={handleSave}
        disabled={saving || (isShopify ? !manualName.trim() : !selectedAttrId)}
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

// ── Grouping Preference Variant Tab ────────────────────────────
function GroupingPreferenceTab() {
  const [toast, showToast] = useToast();
  const [selectedAttrId, setSelectedAttrId] = useState('');
  const [selectedValues, setSelectedValues] = useState([]);
  const [manualName, setManualName] = useState('');
  const [manualValueInput, setManualValueInput] = useState('');

  const isShopify = ['shopify', 'etailon'].includes(sessionStorage.getItem('websiteType'));

  const { data: attrsData, loading: attrsLoading } = useQuery(GET_ATTRIBUTES, { fetchPolicy: 'network-only', skip: isShopify });
  const { loading: savedLoading } = useQuery(GET_GROUP_PREFERENCE_VARIANT, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const s = data?.getGroupPreferenceVariant?.setting;
      if (s) {
        if (isShopify) {
          setManualName(s.attributeName || s.attributeId || '');
        } else {
          setSelectedAttrId(s.attributeId);
        }
        setSelectedValues(s.fieldValues?.map((fv) => ({ id: fv.id, value: fv.value })) || []);
      }
    },
  });
  const [save, { loading: saving }] = useMutation(SAVE_GROUP_PREFERENCE_VARIANT);

  const attributes = attrsData?.getAttributes?.attributes || [];
  const activeAttr = attributes.find((a) => a.id === selectedAttrId);

  const handleAttrChange = (attrId) => {
    setSelectedAttrId(attrId);
    setSelectedValues([]);
  };

  const toggleFieldValue = (fv) => {
    setSelectedValues((prev) => {
      const exists = prev.find((v) => v.id === fv.id);
      return exists ? prev.filter((v) => v.id !== fv.id) : [...prev, { id: fv.id, value: fv.value }];
    });
  };

  const addManualValue = () => {
    const val = manualValueInput.trim();
    if (!val) return;
    if (selectedValues.find((v) => v.value === val)) return showToast('Value already exists', 'error');
    setSelectedValues((prev) => [...prev, { id: val, value: val }]);
    setManualValueInput('');
  };

  const removeManualValue = (id) => {
    setSelectedValues((prev) => prev.filter((v) => v.id !== id));
  };

  const handleSave = async () => {
    if (isShopify) {
      if (!manualName.trim()) return showToast('Enter a name first', 'error');
      const name = manualName.trim();
      const res = await save({
        variables: {
          attributeId: name,
          attributeName: name,
          fieldValues: selectedValues,
        },
      });
      if (res?.data?.saveGroupPreferenceVariant?.error) {
        showToast(res.data.saveGroupPreferenceVariant.message || 'Failed to save', 'error');
      } else {
        showToast('Grouping preference variant saved');
      }
    } else {
      if (!selectedAttrId) return showToast('Select an attribute first', 'error');
      const attr = attributes.find((a) => a.id === selectedAttrId);
      const res = await save({
        variables: {
          attributeId: selectedAttrId,
          attributeName: attr?.labelName || '',
          fieldValues: selectedValues,
        },
      });
      if (res?.data?.saveGroupPreferenceVariant?.error) {
        showToast(res.data.saveGroupPreferenceVariant.message || 'Failed to save', 'error');
      } else {
        showToast('Grouping preference variant saved');
      }
    }
  };

  if ((!isShopify && attrsLoading) || savedLoading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />

      <div className="pref-header">
        <h3 className="settings-section-title">Grouping Preference Variant</h3>
        <p className="settings-section-desc">
          {isShopify
            ? 'Enter a preference name and add values to use as grouping variant preferences.'
            : 'Select an attribute and choose which values to use as grouping variant preferences.'}
        </p>
      </div>

      {isShopify ? (
        <>
          <div className="pref-field">
            <label className="pref-label">Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Size, Color"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
            />
          </div>

          <div className="pref-field">
            <label className="pref-label">Field Values</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Enter a value"
                value={manualValueInput}
                onChange={(e) => setManualValueInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addManualValue()}
              />
              <button className="btn btn--primary" onClick={addManualValue} type="button">Add</button>
            </div>
            {selectedValues.length > 0 && (
              <div className="pref-values-list">
                {selectedValues.map((fv) => (
                  <span key={fv.id} className="pref-value-item pref-value-item--checked" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {fv.value}
                    <button
                      type="button"
                      onClick={() => removeManualValue(fv.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px', lineHeight: 1 }}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="pref-field">
            <label className="pref-label">Attribute</label>
            <select
              className="pref-select"
              value={selectedAttrId}
              onChange={(e) => handleAttrChange(e.target.value)}
            >
              <option value="">-- Select an attribute --</option>
              {attributes.map((attr) => (
                <option key={attr.id} value={attr.id}>{attr.labelName}</option>
              ))}
            </select>
          </div>

          {activeAttr && (
            <div className="pref-field">
              <label className="pref-label">Field Values <span className="pref-label-hint">(multi-select)</span></label>
              <div className="pref-values-list">
                {activeAttr.fieldValues?.map((fv) => {
                  const checked = !!selectedValues.find((v) => v.id === fv.id);
                  return (
                    <label key={fv.id} className={`pref-value-item ${checked ? 'pref-value-item--checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFieldValue(fv)}
                        className="pref-checkbox"
                      />
                      {fv.value}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      <button
        className="btn btn--primary"
        style={{ alignSelf: 'flex-start' }}
        onClick={handleSave}
        disabled={saving || (isShopify ? !manualName.trim() : !selectedAttrId)}
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

// ── Product Category Tab ───────────────────────────────────────
function ProductCategoryTab() {
  const [toast, showToast] = useToast();
  const [selectedTypes, setSelectedTypes] = useState({});
  const [showSelector, setShowSelector] = useState(false);
  const [search, setSearch] = useState('');

  // Admin's saved categories
  const { data, loading, refetch } = useQuery(GET_PRODUCT_CATEGORIES, { fetchPolicy: 'network-only' });
  const [saveFromMaster, { loading: saving }] = useMutation(SAVE_PRODUCT_CATEGORY_FROM_MASTER);
  const [deleteProductCategory] = useMutation(DELETE_PRODUCT_CATEGORY);

  // Master categories available for selection
  const { data: masterData, loading: masterLoading } = useQuery(GET_MASTER_PRODUCT_CATEGORIES_FOR_CLIENT, { fetchPolicy: 'network-only' });

  const savedCategories = data?.getProductCategories?.productCategories || [];
  const masterCategories = masterData?.getMasterProductCategoriesForClient?.masterProductCategories || [];
  const savedMasterIds = savedCategories.filter((c) => c.masterCategoryId).map((c) => c.masterCategoryId);
  const unsavedMasterCategories = masterCategories.filter((mc) => !savedMasterIds.includes(mc.id));
  const q = search.trim().toLowerCase();
  const filteredUnsavedMasterCategories = q
    ? unsavedMasterCategories.filter((mc) =>
        (mc.name || '').toLowerCase().includes(q) ||
        (mc.types || []).some((t) => (t || '').toLowerCase().includes(q)))
    : unsavedMasterCategories;

  const toggleType = (masterCatId, typeName) => {
    setSelectedTypes((prev) => {
      const current = prev[masterCatId] || [];
      return {
        ...prev,
        [masterCatId]: current.includes(typeName) ? current.filter((t) => t !== typeName) : [...current, typeName],
      };
    });
  };

  const toggleAllTypes = (masterCatId, allTypes) => {
    setSelectedTypes((prev) => {
      const current = prev[masterCatId] || [];
      return {
        ...prev,
        [masterCatId]: current.length === allTypes.length ? [] : [...allTypes],
      };
    });
  };

  const handleSave = async (mc) => {
    const types = selectedTypes[mc.id] || [];
    if (types.length === 0 && mc.types && mc.types.length > 0) {
      showToast('Please select at least one type', 'error');
      return;
    }
    const res = await saveFromMaster({ variables: { masterCategoryId: mc.id, name: mc.name, types, optionalTypes: mc.optionalTypes || [], resolution: mc.resolution || 'default' } });
    if (res.data?.saveProductCategoryFromMaster?.error) {
      showToast(res.data.saveProductCategoryFromMaster.message, 'error');
    } else {
      showToast('Category saved');
      setSelectedTypes((prev) => { const next = { ...prev }; delete next[mc.id]; return next; });
      refetch();
    }
  };

  const handleDelete = async (id) => {
    const res = await deleteProductCategory({ variables: { id } });
    if (res.data?.deleteProductCategory?.error) showToast(res.data.deleteProductCategory.message, 'error');
    else { showToast('Removed'); refetch(); }
  };

  if (loading || masterLoading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-section">
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => { }} />

      <div className="settings-section-header">
        <h3 className="settings-section-title">Product Categories</h3>
        {unsavedMasterCategories.length > 0 && (
          <button className="btn btn--primary" onClick={() => setShowSelector(true)}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Category
          </button>
        )}
      </div>

      <AddDrawer
        open={showSelector}
        onClose={() => { setShowSelector(false); setSearch(''); }}
        title="Add Category"
        subtitle="Select categories and types to add to your store"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search categories or types…"
      >
        {unsavedMasterCategories.length === 0 ? (
          <EmptyDrawerMessage>No master categories available to add.</EmptyDrawerMessage>
        ) : filteredUnsavedMasterCategories.length === 0 ? (
          <EmptyDrawerMessage>No categories match "{search}".</EmptyDrawerMessage>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filteredUnsavedMasterCategories.map((mc) => {
              const selected = selectedTypes[mc.id] || [];
              const hasTypes = mc.types && mc.types.length > 0;
              return (
                <div key={mc.id} style={{ border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.75rem 1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: '0.92rem', color: '#1e293b' }}>{mc.name}</strong>
                    <button
                      className="btn btn--primary"
                      style={{ padding: '0.3rem 0.75rem', fontSize: '0.82rem' }}
                      onClick={() => handleSave(mc)}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {hasTypes && (
                    <div style={{ marginTop: '8px' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: '#64748b', cursor: 'pointer', marginBottom: '6px' }}>
                        <input
                          type="checkbox"
                          checked={selected.length === mc.types.length}
                          onChange={() => toggleAllTypes(mc.id, mc.types)}
                        />
                        Select all
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {mc.types.map((t) => {
                          const isOptional = (mc.optionalTypes || []).includes(t);
                          return (
                            <label key={t} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer',
                              background: selected.includes(t) ? '#6366f115' : '#f8fafc',
                              border: selected.includes(t) ? '1px solid #6366f150' : '1px solid #e2e8f0',
                              borderRadius: '4px', padding: '3px 10px', fontSize: '0.84rem',
                            }}>
                              <input
                                type="checkbox"
                                checked={selected.includes(t)}
                                onChange={() => toggleType(mc.id, t)}
                                style={{ accentColor: '#6366f1' }}
                              />
                              {t}
                              <span style={{
                                fontSize: '0.68rem', fontWeight: 600, padding: '1px 6px',
                                borderRadius: '3px',
                                background: isOptional ? '#94a3b820' : '#10b98120',
                                color: isOptional ? '#64748b' : '#059669',
                              }}>{isOptional ? 'Optional' : 'Required'}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </AddDrawer>

      {/* Saved Categories */}
      {savedCategories.length > 0 && (
        <>
          <div className="settings-section-header" style={{ marginTop: '16px' }}>
            <h3 className="settings-section-title">Your Categories</h3>
          </div>
          <div className="dim-list">
            {savedCategories.map((c) => (
              <div key={c.id} className="dim-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="dim-item-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="dim-item-name">{c.name}</span>
                    {(() => {
                      const res = c.resolution || 'default';
                      const isDefault = res === 'default';
                      return (
                        <span style={{
                          background: isDefault ? '#64748b10' : '#6366f110',
                          color: isDefault ? '#475569' : '#6366f1',
                          borderRadius: '4px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
                        }}>
                          {isDefault ? 'Default' : res}
                        </span>
                      );
                    })()}
                  </div>
                  <button className="row-btn row-btn--delete" onClick={() => handleDelete(c.id)}>
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                {c.types && c.types.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', paddingLeft: '4px' }}>
                    {c.types.map((t) => {
                      const isOptional = (c.optionalTypes || []).includes(t);
                      return (
                        <span key={t} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          background: 'var(--color-bg-tertiary, #f0f0f0)', borderRadius: '4px',
                          padding: '2px 8px', fontSize: '13px'
                        }}>
                          {t}
                          <span style={{
                            fontSize: '0.68rem', fontWeight: 600, padding: '1px 6px',
                            borderRadius: '3px',
                            background: isOptional ? '#94a3b820' : '#10b98120',
                            color: isOptional ? '#64748b' : '#059669',
                          }}>{isOptional ? 'Optional' : 'Required'}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Settings Page ─────────────────────────────────────────
export default function Settings() {
  const [activeTab, setActiveTab] = useState('model');

  return (
    <div className="settings-page">
      <header className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Settings</h1>
          <p className="dashboard-subtitle">Configure your listing app experience</p>
        </div>
      </header>

      <div className="settings-body">
        <div className="settings-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`settings-tab ${activeTab === tab.key ? 'settings-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="settings-content">
          {activeTab === 'store' && <StoreTab />}
          {activeTab === 'model' && <ModelTab />}
          {activeTab === 'poses' && <PosesTab />}
          {activeTab === 'modelExpression' && <ModelExpressionTab />}
          {activeTab === 'position' && <PositionTab />}
          {activeTab === 'background' && <BackgroundTab />}
          {activeTab === 'lighting' && <LightingTab />}
          {activeTab === 'dimensions' && <DimensionsTab />}
          {activeTab === 'logo' && <LogoTab />}
          {activeTab === 'text' && <TextTab />}
          {activeTab === 'preference' && <PreferenceTab />}
          {activeTab === 'groupPreference' && <GroupingPreferenceTab />}
          {activeTab === 'productCategory' && <ProductCategoryTab />}
          {activeTab === 'descriptionTemplate' && <DescriptionTemplateTab />}
        </div>
      </div>
    </div>
  );
}
